import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import { MeetingRoom, Videocam } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  format,
  startOfDay,
  endOfDay,
  addDays,
  eachDayOfInterval,
  parseISO,
  differenceInMinutes,
  isSameDay,
} from 'date-fns';
import { getAuthToken } from '../../lib/authToken';

interface ResourceBookingViewProps {
  currentDate: Date;
  onSlotClick?: (resourceId: string, start: Date, end: Date) => void;
  onBookingClick?: (booking: any) => void;
  days?: number;
}

const HOURS = Array.from({ length: 11 }, (_, i) => i + 8); // 8am–6pm
const PX_PER_HOUR = 48;

const ResourceBookingView: React.FC<ResourceBookingViewProps> = ({
  currentDate,
  onSlotClick,
  onBookingClick,
  days = 1,
}) => {
  const rangeStart = startOfDay(currentDate);
  const rangeEnd = endOfDay(addDays(currentDate, days - 1));

  const { data, isLoading, error } = useQuery({
    queryKey: ['resource-bookings', rangeStart.toISOString(), rangeEnd.toISOString()],
    queryFn: async () => {
      const params = new URLSearchParams({
        start: rangeStart.toISOString(),
        end: rangeEnd.toISOString(),
      });
      const res = await fetch(`/api/resources/bookings?${params}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error('Failed to load resource bookings');
      return res.json();
    },
  });

  const resources = data?.data?.resources || [];
  const bookings = data?.data?.bookings || [];
  const dayList = useMemo(
    () => eachDayOfInterval({ start: rangeStart, end: rangeEnd }),
    [rangeStart.getTime(), rangeEnd.getTime()]
  );

  const bookingsFor = (resourceId: string, day: Date) =>
    bookings.filter((b: any) => {
      if (b.resourceId !== resourceId) return false;
      const start = parseISO(b.startDate);
      return isSameDay(start, day) || (parseISO(b.endDate) > startOfDay(day) && start < endOfDay(day));
    });

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load resource schedule.</Alert>;
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
        Resource schedule — {format(currentDate, 'EEEE, MMM d, yyyy')}
      </Typography>

      {resources.length === 0 ? (
        <Alert severity="info">No bookable resources yet. Defaults will be created on first use.</Alert>
      ) : (
        <Stack spacing={2}>
          {resources.map((resource: any) => (
            <Paper key={resource.id} variant="outlined" sx={{ p: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                {resource.type === 'zoom_bridge' ? (
                  <Videocam sx={{ color: resource.color }} />
                ) : (
                  <MeetingRoom sx={{ color: resource.color }} />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {resource.name}
                </Typography>
                <Chip
                  size="small"
                  label={resource.type === 'zoom_bridge' ? 'Zoom / Video' : 'Conference room'}
                  variant="outlined"
                />
                {resource.capacity && (
                  <Typography variant="caption" color="text.secondary">
                    Cap. {resource.capacity}
                  </Typography>
                )}
                {resource.location && (
                  <Typography variant="caption" color="text.secondary">
                    · {resource.location}
                  </Typography>
                )}
              </Box>

              {dayList.map((day) => (
                <Box key={day.toISOString()} sx={{ mb: dayList.length > 1 ? 2 : 0 }}>
                  {days > 1 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      {format(day, 'EEE MMM d')}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      position: 'relative',
                      height: HOURS.length * PX_PER_HOUR,
                      bgcolor: 'grey.50',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      cursor: onSlotClick ? 'pointer' : 'default',
                    }}
                    onClick={(e) => {
                      if (!onSlotClick) return;
                      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                      const y = e.clientY - rect.top;
                      const hourOffset = Math.floor(y / PX_PER_HOUR);
                      const hour = HOURS[Math.min(Math.max(hourOffset, 0), HOURS.length - 1)];
                      const start = new Date(day);
                      start.setHours(hour, 0, 0, 0);
                      const end = new Date(start);
                      end.setHours(hour + 1, 0, 0, 0);
                      onSlotClick(resource.id, start, end);
                    }}
                  >
                    {HOURS.map((hour, idx) => (
                      <Box
                        key={hour}
                        sx={{
                          position: 'absolute',
                          top: idx * PX_PER_HOUR,
                          left: 0,
                          right: 0,
                          height: PX_PER_HOUR,
                          borderTop: idx === 0 ? 'none' : '1px dashed',
                          borderColor: 'divider',
                          pl: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          {format(new Date().setHours(hour, 0, 0, 0), 'h a')}
                        </Typography>
                      </Box>
                    ))}

                    {bookingsFor(resource.id, day).map((booking: any) => {
                      const start = parseISO(booking.startDate);
                      const end = parseISO(booking.endDate);
                      const dayStart = new Date(day);
                      dayStart.setHours(HOURS[0], 0, 0, 0);
                      const dayEnd = new Date(day);
                      dayEnd.setHours(HOURS[HOURS.length - 1] + 1, 0, 0, 0);
                      const clippedStart = start < dayStart ? dayStart : start;
                      const clippedEnd = end > dayEnd ? dayEnd : end;
                      const topMins = differenceInMinutes(clippedStart, dayStart);
                      const durMins = Math.max(differenceInMinutes(clippedEnd, clippedStart), 30);
                      const top = (topMins / 60) * PX_PER_HOUR;
                      const height = (durMins / 60) * PX_PER_HOUR;

                      return (
                        <Tooltip
                          key={booking.id}
                          title={`${booking.title} · ${format(start, 'h:mm a')}–${format(end, 'h:mm a')}`}
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              onBookingClick?.(booking);
                            }}
                            sx={{
                              position: 'absolute',
                              left: 56,
                              right: 8,
                              top,
                              height,
                              bgcolor: resource.color || 'primary.main',
                              color: '#fff',
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              overflow: 'hidden',
                              fontSize: 12,
                              fontWeight: 600,
                              boxShadow: 1,
                              zIndex: 1,
                              cursor: 'pointer',
                            }}
                          >
                            {booking.title}
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              ))}
            </Paper>
          ))}
        </Stack>
      )}
    </Box>
  );
};

export default ResourceBookingView;
